import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Language } from '@/lib/translations/index';
import 'flag-icons/css/flag-icons.min.css';

const languages: { code: Language; label: string; countryCode: string }[] = [
  { code: 'en', label: 'English', countryCode: 'gb' },
  { code: 'pt', label: 'Português', countryCode: 'br' },
  { code: 'es', label: 'Español', countryCode: 'es' },
  { code: 'fr', label: 'Français', countryCode: 'fr' },
  { code: 'ja', label: '日本語', countryCode: 'jp' },
];

export function LanguageToggle() {
  const { language, setLanguage, t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipDisabled, setTooltipDisabled] = useState(false);

  // Disable tooltip when dropdown opens, and keep it disabled briefly after closing
  useEffect(() => {
    if (dropdownOpen) {
      setTooltipOpen(false);
      setTooltipDisabled(true);
    } else {
      const timer = setTimeout(() => {
        setTooltipDisabled(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        const currentIndex = languages.findIndex((l) => l.code === language);
        const nextIndex = (currentIndex + 1) % languages.length;
        setLanguage(languages[nextIndex].code);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [language, setLanguage]);

  const currentLang = languages.find((l) => l.code === language);

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <Tooltip 
        open={tooltipDisabled ? false : tooltipOpen} 
        onOpenChange={(open) => {
          if (!tooltipDisabled) {
            setTooltipOpen(open);
          }
        }}
      >
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-primary/10"
            >
              <span className={`fi fi-${currentLang?.countryCode} rounded-sm`} style={{ fontSize: '16px' }} />
              <span className="sr-only">{t('ui.languageToggle.selectLanguage')}</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('ui.languageToggle.changeLanguage')}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="bg-popover">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={language === lang.code ? 'bg-accent' : ''}
          >
            <span className={`fi fi-${lang.countryCode} mr-2 rounded-sm`} style={{ fontSize: '16px' }} />
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
